import { setCoachFeedChaosMode } from '~/utils/coach/feedChaos';

import type { useWebSocketTestMode } from './useWebSocketTestMode';

/**
 * Suspends the coach feed: every frame is swallowed while the block is on, so the session keeps
 * moving server-side and the tab hears none of it. Lifting the block replays the reconnect path,
 * and the catch-up read from the last seq held is what restores the transcript.
 */
export const useWebSocketBlockMode = (testMode: ReturnType<typeof useWebSocketTestMode>) => {
  if (!testMode) {
    return null;
  }

  const { setMode, isBlockMode } = testMode;

  const blockedAt = ref<number | null>(null);
  const duration = ref(0);

  let durationTimer: ReturnType<typeof setInterval> | null = null;

  const unblock = () => {
    setMode('none');
    setCoachFeedChaosMode('none');

    blockedAt.value = null;
    duration.value = 0;

    if (durationTimer) {
      clearInterval(durationTimer);
      durationTimer = null;
    }
  };

  const block = () => {
    if (isBlockMode.value) {
      return;
    }

    setMode('block');
    setCoachFeedChaosMode('block');

    blockedAt.value = Date.now();
    duration.value = 0;

    durationTimer = setInterval(() => {
      if (blockedAt.value) {
        duration.value = Math.floor((Date.now() - blockedAt.value) / 1000);
      }
    }, 1000);
  };

  const toggle = () => {
    if (isBlockMode.value) {
      unblock();
    }
    else {
      block();
    }
  };

  onUnmounted(() => {
    if (isBlockMode.value) {
      unblock();
    }
    else if (durationTimer) {
      clearInterval(durationTimer);
    }
  });

  return {
    isActive: isBlockMode,
    blockedAt: readonly(blockedAt),
    duration: readonly(duration),
    block,
    unblock,
    toggle,
  };
};
